import React, { useState } from 'react';
import { runAiJob } from '../../lib/aiJob';
import { useAuth } from '../../context/AuthContext';
import { getActiveMatter } from '../../lib/activeMatter';
import { toast } from 'sonner';
import { Scale, Loader2, Trophy } from 'lucide-react';

const pct = (v) => `${Math.round((v || 0) * 100)}%`;
const SEL = 'w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white';

const Side = ({ side, label, tactics, favorable, caseType, testid }) => {
  const rate = side.win_rate_by_type?.[caseType]?.win_rate;
  return (
    <div data-testid={testid} className={`rounded-xl p-4 border ${favorable ? 'border-green-500/50 bg-green-900/10' : 'border-slate-700 bg-slate-900/50'}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs text-slate-500">Juez {label}</p>
          <p className="text-white font-semibold">{side.judge.name}</p>
          <p className="text-xs text-slate-400">{side.judge.court_name}</p>
        </div>
        <div className="text-right">
          <p className={`text-3xl font-bold ${favorable ? 'text-green-400' : 'text-slate-200'}`}>{pct(rate)}</p>
          {favorable && <p className="text-xs text-green-400 flex items-center gap-1 justify-end"><Trophy className="w-3 h-3" />Más favorable</p>}
        </div>
      </div>
      <p className="text-xs font-semibold text-slate-300 mt-3 mb-1">Estrategia ante este juez</p>
      <ul className="text-sm text-slate-300 space-y-1">{(tactics || []).map((t, i) => <li key={i}>• {t}</li>)}</ul>
    </div>
  );
};

export const CompareJudges = ({ judges, caseTypes }) => {
  const { api } = useAuth();
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [caseType, setCaseType] = useState(() => { const m = getActiveMatter(); return m?.matter_type && m.matter_type !== 'disciplinario' ? m.matter_type : 'laboral'; });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!a || !b || a === b) return toast.error('Elige dos jueces distintos');
    setLoading(true);
    try {
      setResult(await runAiJob(api, '/judicial-intelligence/compare', { judge_a: a, judge_b: b, case_type: caseType }));
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Error al comparar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="compare-judges-section" className="bg-slate-800/50 rounded-xl p-6 border border-slate-700 mb-8">
      <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2"><Scale className="w-6 h-6 text-cyan-400" />Comparar Jueces</h2>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <select data-testid="compare-judge-a" value={a} onChange={(e) => setA(e.target.value)} className={SEL}>
          <option value="">Juez A...</option>
          {judges.map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}
        </select>
        <select data-testid="compare-judge-b" value={b} onChange={(e) => setB(e.target.value)} className={SEL}>
          <option value="">Juez B...</option>
          {judges.map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}
        </select>
        <select data-testid="compare-case-type" value={caseType} onChange={(e) => setCaseType(e.target.value)} className={SEL}>
          {caseTypes.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <button data-testid="compare-btn" onClick={run} disabled={loading} className="bg-cyan-600 hover:bg-cyan-500 text-white font-semibold py-2 px-4 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" />Comparando...</> : 'Comparar'}
        </button>
      </div>
      {result && (
        <div className="mt-6 space-y-4">
          <p data-testid="compare-conclusion" className="text-white bg-cyan-900/20 border border-cyan-500/30 rounded-xl p-4 text-sm">{result.ai.conclusion}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Side testid="compare-side-a" side={result.a} label="A" tactics={result.ai.estrategia_a} favorable={result.ai.favorable === 'A'} caseType={result.case_type} />
            <Side testid="compare-side-b" side={result.b} label="B" tactics={result.ai.estrategia_b} favorable={result.ai.favorable === 'B'} caseType={result.case_type} />
          </div>
          <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700">
            <p className="text-xs font-semibold text-slate-300 mb-2">Diferencias clave de criterio</p>
            <ul className="text-sm text-slate-400 space-y-1">{(result.ai.diferencias || []).map((d, i) => <li key={i}>• {d}</li>)}</ul>
          </div>
        </div>
      )}
    </div>
  );
};
